import React from 'react'
import { Autocomplete, Box, Button, Divider, MenuItem, Tooltip } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import SaveIcon from '@mui/icons-material/Save'
import CleaningServicesIcon from '@mui/icons-material/CleaningServices'
import CancelIcon from '@mui/icons-material/Cancel'
import SearchIcon from '@mui/icons-material/Search'
import { FilterRow, FilterSection, StyledTextField, DateInput, DarkButton } from '../../../ui/StyledComponents'
import { plateNumbers, transportLabel } from '../../../helpers/logisticaHelper'
import { renderTransportOption } from './transportOption'
import ClienteAutocomplete from './ClienteAutocomplete'

export default function PlanillaRepartoSelector({
    cabecera,
    readOnly,
    canGuardar,
    fechaCargaError,
    onCabeceraChange,
    onTransportChange,
    onNuevaPatente,
    filtro,
    onFiltroChange,
    transports,
    onFiltrar,
    onGuardar,
    onLimpiar,
    onAnular,
    onBuscar,
}) {
    // Una planilla guardada con un transporte que hoy está inactivo lo sigue mostrando
    const transportOptions =
        cabecera.transportCode && !transports.some((t) => t.code === cabecera.transportCode)
            ? [...transports, { code: cabecera.transportCode, plates: [] }]
            : transports
    const transportSeleccionado = transportOptions.find((t) => t.code === cabecera.transportCode) || null
    // Una planilla guardada con una patente que hoy no está en el transporte la sigue mostrando
    const plateOptions = plateNumbers(transportSeleccionado)
    if (cabecera.plate && !plateOptions.includes(cabecera.plate)) plateOptions.push(cabecera.plate)
    const puedeCargarPatente = !readOnly && !!cabecera.transportCode

    return (
        <FilterSection sx={{ mb: 3 }}>
            <FilterRow>
                <StyledTextField
                    size="small"
                    label="Planilla de reparto"
                    value={cabecera.sheetId ?? ''}
                    InputProps={{ readOnly: true }}
                    sx={{ flex: 1 }}
                />
                 <DateInput
                    label="Fecha de carga"
                    value={cabecera.loadDate}
                    onChange={(value) => onCabeceraChange('loadDate', value)}
                    disabled={readOnly}
                    helperText={fechaCargaError}
                    size="small"
                  />
                <DateInput
                    label="Fecha de reparto"
                    value={cabecera.deliveryDate}
                    onChange={(value) => onCabeceraChange('deliveryDate', value)}
                    disabled={readOnly}
                    size="small"
                />
                <Autocomplete
                    size="small"
                    options={transportOptions}
                    value={transportSeleccionado}
                    getOptionLabel={transportLabel}
                    isOptionEqualToValue={(a, b) => a.code === b.code}
                    renderOption={renderTransportOption}
                    onChange={(_, value) => onTransportChange(value)}
                    disabled={readOnly}
                    sx={{ flex: 1.5 }}
                    renderInput={(params) => <StyledTextField {...params} label="Transporte" />}
                />
                {/* La patente se elige de las del transporte; una nueva se crea solo con el botón + */}
                <Box sx={{ display: 'flex', gap: 0.5, flex: 1 }}>
                    <StyledTextField
                        select
                        size="small"
                        label="Patente"
                        value={cabecera.plate ?? ''}
                        onChange={(e) => onCabeceraChange('plate', e.target.value)}
                        disabled={readOnly || !cabecera.transportCode}
                        sx={{ flex: 1 }}
                    >
                        <MenuItem value=""><em>Sin patente</em></MenuItem>
                        {plateOptions.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                    </StyledTextField>
                    <Tooltip title={puedeCargarPatente ? 'Nueva patente' : 'Seleccioná un transporte para cargar una patente'}>
                        <span>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={onNuevaPatente}
                                disabled={!puedeCargarPatente}
                                aria-label="Nueva patente"
                                sx={{ minWidth: 42, width: 42, height: 42, p: 0 }}
                            >
                                <AddIcon />
                            </Button>
                        </span>
                    </Tooltip>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                    <DarkButton startIcon={<SaveIcon />} onClick={onGuardar} disabled={!canGuardar} sx={{ whiteSpace: 'nowrap' }}>
                        Guardar
                    </DarkButton>
                    <Button variant="outlined" startIcon={<CleaningServicesIcon />} onClick={onLimpiar} sx={{ whiteSpace: 'nowrap' }}>
                        Limpiar
                    </Button>
                    <Button variant="outlined" color="error" startIcon={<CancelIcon />} onClick={onAnular} disabled={!cabecera.sheetId || readOnly} sx={{ whiteSpace: 'nowrap' }}>
                        Anular
                    </Button>
                    <Button variant="outlined" startIcon={<SearchIcon />} onClick={onBuscar} sx={{ whiteSpace: 'nowrap' }}>
                        Buscar
                    </Button>
                </Box>
            </FilterRow>

            <Divider />

            <FilterRow>
                <DateInput
                    label="Fecha documento desde"
                    value={filtro.fechaDesde}
                    onChange={(value) => onFiltroChange('fechaDesde', value)}
                    disabled={readOnly}
                    size="small"
                />
                <DateInput
                    label="Fecha documento hasta"
                    value={filtro.fechaHasta}
                    onChange={(value) => onFiltroChange('fechaHasta', value)}
                    disabled={readOnly}
                    size="small"
                />
                <ClienteAutocomplete
                    value={filtro.customer}
                    onChange={(value) => onFiltroChange('customer', value)}
                    disabled={readOnly}
                    sx={{ flex: 1.5 }}
                />
                <DarkButton
                    onClick={onFiltrar}
                    disabled={readOnly || !filtro.fechaDesde || !filtro.fechaHasta || !cabecera.transportCode}
                    sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                >
                    Filtrar
                </DarkButton>
                <Box sx={{ flex: 1.5 }} />
            </FilterRow>
        </FilterSection>
    )
}
